import { ref, onMounted, toRef, reactive, watch } from 'vue';
import { formatDateToPtBR, parseBoolean, parseDate } from '@/shared/lib';
import type { BaseFilterData } from '@/shared/lib';

/**
 * O campo de revisão é um stepper: ele representa um inteiro, não a faixa `N-M` que as filas de
 * homologação aceitam na URL. `Number('2-4')` é `NaN`, e `NaN` atravessa o mapeador de saída, some
 * na URL como a string `'NaN'` e ainda conta como filtro ativo. Valor que o stepper não representa
 * volta ao padrão.
 */
const toStepperRevision = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

export function useGenericFilterModal<T extends BaseFilterData>(
  // Note: emitFunc uses any[] due to Vue's strict component-specific emit types
  // being incompatible with generic typing. This is a known Vue 3 limitation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emitFunc: (...args: any[]) => void,
  defaultFormData: T,
  fetchUsersFunc?: () => Promise<void>,
  initialFilters?: Partial<T>,
  loadMapper?: (filters: Partial<T>) => T
) {
  const formData = ref({ ...defaultFormData });

  const dateRefs = reactive({
    elaboration: null as Date | null,
    approval: null as Date | null,
    effective: null as Date | null,
    request: null as Date | null,
    validity: null as Date | null,
    verification: null as Date | null,
  });

  const modalStates = reactive({
    isElaborationDateOpen: false,
    isRequestDateOpen: false,
    isApprovalDateOpen: false,
    isEffectiveDateOpen: false,
    isVerificationDateOpen: false,
    isValidityDateOpen: false,
  });

  const loadFilters = (filters?: Partial<T>) => {
    if (!filters || Object.keys(filters).length === 0) return;

    try {
      const mappedFilters = loadMapper ? loadMapper(filters) : filters;

      formData.value = {
        ...defaultFormData,
        ...mappedFilters,
        // Parse boolean fields from URL query params
        isExternal: parseBoolean(filters.isExternal),
        hasTraining: parseBoolean(filters.hasTraining),
        // Parse numeric fields
        revision: toStepperRevision(filters.revision),
        // Parse date fields
        elaborationDate: parseDate(filters.elaborationDate),
        requestDate: parseDate(filters.requestDate),
        approvalDate: parseDate(filters.approvalDate),
        effectiveDate: parseDate(filters.effectiveDate),
        validityDate: parseDate(filters.validityDate),
        verificationDate: parseDate(filters.verificationDate),
      };

      dateRefs.elaboration = parseDate(filters.elaborationDate);
      dateRefs.approval = parseDate(filters.approvalDate);
      dateRefs.effective = parseDate(filters.effectiveDate);
      dateRefs.request = parseDate(filters.requestDate);
      dateRefs.validity = parseDate(filters.validityDate);
      dateRefs.verification = parseDate(filters.verificationDate);
    } catch {
      formData.value = { ...defaultFormData };
    }
  };

  onMounted(async () => {
    if (fetchUsersFunc) {
      try {
        await fetchUsersFunc();
      } catch {
        // Silent fail - users list won't be available
      }
    }
    loadFilters(initialFilters);
  });

  watch(
    () => initialFilters,
    (newFilters?: Partial<T>) => {
      if (newFilters) {
        loadFilters(newFilters);
      }
    },
    { immediate: true, deep: true }
  );

  const applyFilters = (mapper?: (formData: T) => Partial<T>) => {
    formData.value = {
      ...formData.value,
      elaborationDate: dateRefs.elaboration,
      approvalDate: dateRefs.approval,
      effectiveDate: dateRefs.effective,
      requestDate: dateRefs.request,
      validityDate: dateRefs.validity,
      verificationDate: dateRefs.verification,
    };

    const filters = mapper ? mapper(formData.value) : formData.value;

    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => {
        if (typeof value === 'boolean') return true;
        return value !== null && value !== undefined && value !== '';
      })
    );

    emitFunc('update:filter', cleanedFilters);
    resetFormToCleanState();
  };

  const resetFormToCleanState = () => {
    Object.assign(formData.value, defaultFormData);
    dateRefs.elaboration = null;
    dateRefs.approval = null;
    dateRefs.effective = null;
    dateRefs.request = null;
    dateRefs.validity = null;
    dateRefs.verification = null;
    modalStates.isRequestDateOpen = false;
    modalStates.isElaborationDateOpen = false;
    modalStates.isApprovalDateOpen = false;
    modalStates.isEffectiveDateOpen = false;
    modalStates.isVerificationDateOpen = false;
    modalStates.isValidityDateOpen = false;
  };

  const clearFilters = () => {
    resetFormToCleanState();
    emitFunc('update:selectedDate', null);
  };

  return {
    formData,
    elaborationDate: toRef(dateRefs, 'elaboration'),
    approvalDate: toRef(dateRefs, 'approval'),
    effectiveDate: toRef(dateRefs, 'effective'),
    requestDate: toRef(dateRefs, 'request'),
    validityDate: toRef(dateRefs, 'validity'),
    verificationDate: toRef(dateRefs, 'verification'),
    isElaborationDateOpen: toRef(modalStates, 'isElaborationDateOpen'),
    isRequestDateOpen: toRef(modalStates, 'isRequestDateOpen'),
    isApprovalDateOpen: toRef(modalStates, 'isApprovalDateOpen'),
    isEffectiveDateOpen: toRef(modalStates, 'isEffectiveDateOpen'),
    isVerificationDateOpen: toRef(modalStates, 'isVerificationDateOpen'),
    isValidityDateOpen: toRef(modalStates, 'isValidityDateOpen'),
    formatDate: formatDateToPtBR,
    applyFilters,
    clearFilters,
    loadFilters,
  };
}
